export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

function getDefaultHeaders(): HeadersInit {
  return {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };
}

function getMutationHeaders(): HeadersInit {
  return {
    ...getDefaultHeaders(),
    "Content-Type": "application/json",
    "X-CSRF-Token": getCsrfToken(),
  };
}

export async function apiGet<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: "GET",
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiError(
      data?.error?.message || data?.error || `Request failed with status ${response.status}`,
      response.status,
      data,
    );
  }

  return response.json();
}

interface ApiPostOptions {
  headers?: HeadersInit;
}

export async function apiPost<T, D = unknown>(
  url: string,
  data?: D,
  options?: ApiPostOptions,
): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      ...getMutationHeaders(),
      ...options?.headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new ApiError(
      errorData?.error?.message || errorData?.error || `Request failed with status ${response.status}`,
      response.status,
      errorData,
    );
  }

  return response.json();
}

export type QueryValue = string | number | boolean | string[] | number[] | undefined;

export function buildQueryString(params: Record<string, QueryValue>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(`${key}[]`, String(item));
      }
    } else {
      searchParams.set(key, String(value));
    }
  }

  return searchParams.toString();
}
